import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DataList from './DataList';
import { useAuth } from '../hooks/useAuth';
import '../styles/NetworkAccessConfig.css';

const NetworkAccessConfig = () => {
  const { hasPermission, token } = useAuth();
  const [currentIp, setCurrentIp] = useState('Loading...');
  const [ipLoadError, setIpLoadError] = useState('');

  const canView = hasPermission('/network-access-config', 'View');

  useEffect(() => {
    const fetchCurrentIp = async () => {
      if (!token) {
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/access/current-ip`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCurrentIp(response.data?.ip_address || 'Unknown');
        setIpLoadError('');
      } catch (error) {
        setCurrentIp('Unavailable');
        setIpLoadError('Unable to load the current IP address.');
      }
    };

    fetchCurrentIp();
  }, [token]);

  const apiConfig = useMemo(() => ({
    title: 'Network Access Config',
    path: '/network-access-config',
    fetchEndpoint: '/hospital-portal/api/network-access',
    createEndpoint: '/hospital-portal/api/network-access',
    updateEndpoint: '/hospital-portal/api/network-access',
    deleteEndpoint: '/hospital-portal/api/network-access',
    defaultSortFieldId: 'ip_address',
    allowAdd: true,
    allowEdit: true,
    allowExport: false,
    staticDropdownFields: [
      {
        name: 'classification',
        options: [
          { id: 'hospital', name: 'Hospital' },
          { id: 'branch', name: 'Branch' },
          { id: 'vpn', name: 'VPN' },
          { id: 'other', name: 'Other' },
        ],
      },
    ],
    fields: [
      { name: 'id', label: 'ID', type: 'text', identifier: true, disabled: true, tab: 'Main' },
      { name: 'ip_address', label: 'IP Address / Subnet', type: 'text', required: true, tab: 'Main' },
      { name: 'description', label: 'Description', type: 'text', tab: 'Main' },
      { name: 'classification', label: 'Classification', type: 'dropdown', required: true, tab: 'Main' },
      { name: 'is_enabled', label: 'Enabled', type: 'checkbox', tab: 'Main' },
      { name: 'createdAt', label: 'Created At', type: 'text', disabled: true, tab: 'Admin' },
      { name: 'updatedAt', label: 'Updated At', type: 'text', disabled: true, tab: 'Admin' },
    ],
    tabs: [
      { id: 'Main', name: 'Main', roles: 'all', default: true },
      { id: 'Admin', name: 'Admin', roles: 'Admin' },
    ],
  }), []);

  const columns = useMemo(() => ([
    { name: 'ID', selector: (row) => row.id, sortable: true, identifier: true, width: '70px' },
    { name: 'IP Address', selector: (row) => row.ip_address, sortable: true, filterable: true, filterSelector: 'ip_address' },
    { name: 'Description', selector: (row) => row.description, sortable: true, filterable: true, filterSelector: 'description' },
    { name: 'Classification', selector: (row) => row.classification, sortable: true, filterable: true, filterSelector: 'classification' },
    { name: 'Enabled', selector: (row) => row.is_enabled, sortable: true, type: 'boolean', filterable: true, filterSelector: 'is_enabled', filterDefault: 1 },
  ]), []);

  if (!canView) {
    return (
      <div className="network-access-page access-denied">
        <h2>Access Denied</h2>
        <p>You do not have permission to view network access configuration.</p>
      </div>
    );
  }

  return (
    <div className="network-access-page">
      <div className="network-access-header">
        <h2>Network Access Config</h2>
        <p className="network-access-current-ip">
          Current logged-in IP address: <strong>{currentIp}</strong>
        </p>
        {ipLoadError && <p className="network-access-ip-error">{ipLoadError}</p>}
      </div>
      <DataList apiConfig={apiConfig} columns={columns} />
    </div>
  );
};

export default NetworkAccessConfig;